import { motion } from 'framer-motion';
import { useEffect } from 'react';
import {
  paginationAnimVariant,
  sectionAnimVariants,
} from '../../../constants/animationVariants';
import { APPOINTMENT_CARDS_PER_PAGE } from '../../../constants/settings';
import { useAppointmentListContext } from '../../../contexts/AppointmentListProvider';
import { useAuthContext } from '../../../contexts/AuthProvider';
import {
  isPastTime,
  isRunningTime,
  isUpcomingTime,
} from '../../../utils/timeAndDate';
import AppointmentCard from '../../CommonComps/Cards/AppointmentCard';
import AcademiaLoading from '../../CommonComps/Loadings/AcademiaLoading';
import NoTableDataFound from '../../CommonComps/NoTableDataFound';
import DashboardTablePagination from '../../CommonComps/Paginations/DashboardTablePagination';
import PageHeader from '../../CommonComps/Titles/PageHeader';

const AppointmentListSection = () => {
  // integration of context hooks here
  const { currentUserType } = useAuthContext();
  const {
    isAppointmentListLoading,
    currentPage,
    appointmentType,
    appointmentList,
    setCurrentPage,
  } = useAppointmentListContext();

  // calculating pages for pagination here
  const totalPages = Math.ceil(
    appointmentList.length / APPOINTMENT_CARDS_PER_PAGE
  );
  const startIndex = (currentPage - 1) * APPOINTMENT_CARDS_PER_PAGE;
  const endIndex = startIndex + APPOINTMENT_CARDS_PER_PAGE;

  // scrolling to the top of the page when current page is changed here
  useEffect(() => {
    if (currentPage) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  }, [currentPage]);

  // rendering appointment history section component here
  return (
    <>
      {isAppointmentListLoading ? (
        <AcademiaLoading />
      ) : appointmentList.length ? (
        <motion.section
          variants={sectionAnimVariants}
          initial='initial'
          animate='animate'
        >
          <PageHeader headerText={`${appointmentType} appointments`} />

          <div className='space-y-5'>
            <motion.div
              key={currentPage}
              variants={paginationAnimVariant}
              initial='initial'
              animate='animate'
              exit='exit'
              className='grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4 gap-5'
            >
              {appointmentList
                .slice(startIndex, endIndex)
                .map((appointment) => (
                  <AppointmentCard
                    appointment={appointment}
                    key={appointment.id}
                    isAssociate={currentUserType === 'user'}
                    isUpcoming={
                      appointmentType === 'upcoming' ||
                      isUpcomingTime(appointment.date, appointment.start_time)
                    }
                    isLive={
                      appointmentType === 'running' ||
                      isRunningTime(
                        appointment.date,
                        appointment.start_time,
                        appointment.end_time
                      )
                    }
                    isUnattended={
                      isPastTime(appointment.date, appointment.end_time) &&
                      !appointment.is_complete
                    }
                  />
                ))}
            </motion.div>

            <div className='flex flex-col md:flex-row items-center justify-center md:justify-between gap-3 bg-legal-dark-blue rounded-xl px-5 py-3 shadow-academia-sm'>
              <p className='text-lg font-medium text-base-white'>
                Showing{' '}
                <span className='font-semibold text-legal-brown'>
                  {startIndex + 1}-
                  {endIndex > appointmentList.length
                    ? appointmentList.length
                    : endIndex}
                </span>{' '}
                of{' '}
                <span className='font-semibold text-legal-brown'>
                  {appointmentList.length}
                </span>
              </p>

              <DashboardTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                setCurrentPage={setCurrentPage}
              />
            </div>
          </div>
        </motion.section>
      ) : (
        <motion.section
          variants={sectionAnimVariants}
          initial='initial'
          animate='animate'
        >
          <PageHeader headerText={`${appointmentType} appointments`} />
          <NoTableDataFound
            message={`no ${
              appointmentType !== 'all'
                ? appointmentType === 'referrals'
                  ? 'referred'
                  : appointmentType
                : ''
            } appointments!!`}
            disableAnim
          />
        </motion.section>
      )}
    </>
  );
};

export default AppointmentListSection;
